import React, { ReactNode } from 'react'
import { PixelRatio } from 'react-native'
import styled from 'styled-components/native'

import DialogueTouchableOpacity from 'APP/Components/DialogueTouchableOpacity'
import Typography from 'APP/Converse/Typography'
import { Images } from 'APP/Themes'
import ContentCardNotice, { ContentNoticeType } from './ContentCardNotice'

const ContentCardContainer = styled(DialogueTouchableOpacity)<{ width: number }>`
  margin: ${({ theme }) => theme.metrics.baseMargin * 0.75}px;
  background-color: ${({ theme }) => theme.colors.elementsBg};
  border-radius: ${({ theme }) => theme.metrics.baseMargin / 2}px;
  width: ${({ width }) => width};
  ${({ theme }) => theme.shadows.soft}
`

const ContentContainer = styled.View<{ maxWidth: number }>`
  padding: ${({ theme }) => theme.metrics.baseMargin}px;
  flex-direction: column;
  justify-content: space-between;
  max-width: ${({ maxWidth }) => maxWidth};
`

const Content = styled.View`
  flex-direction: row;
  align-items: center;
  margin-top: ${({ theme }) => theme.metrics.baseMargin / 2}px;
  gap: ${({ theme }) => theme.metrics.baseMargin / 4}px;
`

const Tag = styled(Typography)`
  text-transform: capitalize;
  padding-left: ${({ theme }) => theme.metrics.baseMargin / 2}px;
`

const ImageContainer = styled.View`
  position: relative;
  overflow: hidden;
  border-top-left-radius: ${({ theme }) => theme.metrics.baseMargin / 2}px;
  border-top-right-radius: ${({ theme }) => theme.metrics.baseMargin / 2}px;
`

const Image = styled.Image`
  aspect-ratio: 2;
  width: '100%';
`

interface ContentCardProps {
  title: string
  onPress: () => void
  icon: ReactNode
  info: string
  imageUrl: string
  notice?: { name: string; type: ContentNoticeType }
  testID?: string
  tag?: string
  maxWidth: number
}

const ContentCard: React.FC<ContentCardProps> = ({
  title,
  onPress,
  icon,
  info,
  imageUrl,
  tag,
  notice,
  maxWidth = 300,
  testID = 'CONTENT_CARD',
}) => {
  const imageWidth = PixelRatio.getPixelSizeForLayoutSize(maxWidth)
  const imageURI = `${imageUrl}?w=${imageWidth}`

  return (
    <ContentCardContainer onPress={onPress} width={maxWidth} testID={testID}>
      <ImageContainer>
        <Image
          source={{ uri: imageURI, cache: 'force-cache' }}
          defaultSource={Images.contentLoading}
          resizeMode="cover"
        />
      </ImageContainer>
      <ContentContainer maxWidth={maxWidth}>
        <Typography variant="h4">{title}</Typography>
        <Content>
          {icon}
          <Typography variant="bodySmall">{info}</Typography>
          {tag && (
            <Tag testID="CONTENT_CARD_TAG" variant="bodySmall">
              {tag}
            </Tag>
          )}
        </Content>
      </ContentContainer>
      {!!notice && <ContentCardNotice {...notice} />}
    </ContentCardContainer>
  )
}

export default ContentCard
